// @flow
import {useSelector} from 'react-redux';
import {useEffect, useMemo, useState} from 'react';

import type {Store, Feature, StoreDomain, StoreFeature} from '../../entities';
import type {RootState} from '../../modules';
import {useUserType} from '../../modules/auth/utils';

export function isFeatureInTier(feature: string, store: Store) {
  if (store && store.features)
    return store.features.find(o => o.name === feature);
}

export function getFeatureSpec(feature: StoreFeature, store: Store) {
  if (store && store.features) {
    const foundFeature: ?Feature = store.features.find(
      (o: Feature) => o.name === feature
    );
    return foundFeature && foundFeature.spec ? foundFeature.spec : undefined;
  }
}

const storeFeaturesSelector = (state: RootState) =>
  state.store.store ? state.store.store.features : [];

export function useIsFeatureInTier(feature: StoreFeature) {
  const storeFeatures = useSelector(storeFeaturesSelector);
  const {isCollaborator} = useUserType();

  const isFeatureInTier = useMemo(() => {
    const exist = storeFeatures.find(o => o.name === feature);

    //validate collaborator features, [feature]_DISABLED
    if (!isCollaborator) return !!exist;

    if (feature.includes('_DISABLED')) return !!exist;

    const disabled = storeFeatures.find(o => o.name === `${feature}_DISABLED`);

    return !!exist && !disabled;
  }, [storeFeatures, isCollaborator, feature]);

  return isFeatureInTier;
}

export function useFeatureSpec(feature: StoreFeature) {
  const reduxStore = useSelector<Store>(state => state.store.store);
  const [_featureSpec, setFeatureSpec] = useState(
    getFeatureSpec(feature, reduxStore)
  );

  useEffect(() => {
    if (reduxStore && reduxStore.features) {
      setFeatureSpec(getFeatureSpec(feature, reduxStore));
    }
  }, [feature, reduxStore]);

  return _featureSpec;
}

export function useStoreFeatures() {
  const storeFeatures = useSelector(storeFeaturesSelector);
  return storeFeatures;
}

export function currentUserHasFeature(
  feature: StoreFeature,
  storeFeatures: Feature[] = [],
  isCollaborator: boolean = false
): boolean {
  const exist = storeFeatures.find(o => o.name === feature);

  // Validate collaborator features, [feature]_DISABLED
  if (!isCollaborator) return !!exist;

  if (feature.includes('_DISABLED')) return !!exist;

  const disabled = storeFeatures.find(o => o.name === `${feature}_DISABLED`);

  return !!exist && !disabled;
}

export function getCustomDomain(store: Store): string {
  const domains = store.domains;
  const hasCustomDomainFeature = isFeatureInTier('CUSTOM_DOMAIN', store);
  const domain = domains.find(d => !d.domainName.endsWith('.tiendada.com'));
  if (!!hasCustomDomainFeature && domain) return domain.domainName;
  else return domains[0].domainName;
}

export function getBasicDomain(domains: StoreDomain[]): string {
  const domain = domains.find(d => d.domainName.endsWith('.tiendada.com'));
  if (domain) return domain.domainName;
  else return domains[0].domainName;
}
